// ============================================================================
// FILE STORAGE SERVICE
// ============================================================================
// Wraps POST /api/v1/Files/upload (multipart) and exposes a helper that builds
// the download URL for an <img src=...> / <a href=...>.

import { axiosClient } from '../client/axiosClient';
import { fileStorageEndpoints } from '../endpoints/fileStorage.endpoints';
import { apiConfig } from '../config/apiConfig';
import type { ApiResponse } from '../types';
import type { StoredFileInfo } from '../types/fileStorage.types';

class FileStorageService {
  /**
   * Uploads a file under the given category. Returns the stored file metadata
   * — caller stores `relativePath` on its domain model.
   */
  async upload(file: File, category: string): Promise<StoredFileInfo> {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await axiosClient.post<ApiResponse<StoredFileInfo>>(
      `${fileStorageEndpoints.upload()}?category=${encodeURIComponent(category)}`,
      form,
      {
        headers: {
          // Let axios pick the right multipart boundary; passing empty
          // value prevents the default 'application/json' from sneaking in.
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    if (!response.data?.data) {
      throw new Error(response.data?.message || 'File upload failed.');
    }
    return response.data.data;
  }

  /**
   * Builds an absolute URL to GET the file. Useful when the consumer can
   * include the Authorization + X-Tenant-ID headers itself (e.g. a fetch
   * inside an authenticated axios call). NOT safe to drop directly into
   * `<img src=...>` — raw img tags don't send custom headers, so the
   * server returns 401 / MISSING_TENANT_ID. Use {@link openAsObjectUrl}
   * for image previews instead.
   */
  getDownloadUrl(relativePath: string): string {
    return `${apiConfig.baseURL}${fileStorageEndpoints.downloadUrl(relativePath)}`;
  }

  /**
   * Fetches the file as a blob through authenticated axios and returns a
   * local `blob:` Object URL safe to use as `<img src>`. The caller MUST
   * revoke the URL when the image unmounts (`URL.revokeObjectURL(url)`)
   * to avoid leaking blob memory.
   */
  async openAsObjectUrl(relativePath: string): Promise<string> {
    const response = await axiosClient.get(
      fileStorageEndpoints.downloadUrl(relativePath),
      { responseType: 'blob' },
    );
    return URL.createObjectURL(response.data);
  }
}

export const fileStorageService = new FileStorageService();
export default fileStorageService;
